/**
 * What an event measures — SIMS-8531.
 *
 * 🔴 These five lists MIRROR the server's closed vocabularies
 * (`@shared/measurements/measurement-vocabulary`) and must stay in step with
 * them. AC5 is enforced on both sides for different reasons: here so the
 * configurator is offered choices rather than a text box, and there so an
 * imported or generated configuration cannot slip a value past the form (AC10).
 *
 * 🔴 THERE IS NO TARGET VALUE HERE, and that is the design (AC11). The number
 * lives in the treatment plan's reward condition, which is where the payment is
 * decided (D55, SIMS-8958) — so a screen cannot disagree with what actually
 * paid, and a plan whose target moves partway through needs no second
 * declaration. A configurator looking for a number field will not find one, and
 * the form says so rather than leaving them hunting.
 */

/** What kind of number the measurement is. */
export enum MeasurementQuantity {
	PERCENTAGE_IN_RANGE = "PERCENTAGE_IN_RANGE",
	AVERAGE_VALUE = "AVERAGE_VALUE",
	SCORE = "SCORE",
	COUNT_OF_OCCURRENCES = "COUNT_OF_OCCURRENCES",
	DURATION = "DURATION",
	DISCRETE_OUTCOME = "DISCRETE_OUTCOME",
}

/** What the number is in. */
export enum MeasurementUnit {
	PERCENT = "PERCENT",
	HOURS = "HOURS",
	MINUTES = "MINUTES",
	COUNT = "COUNT",
	MG_DL = "MG_DL",
	KCAL = "KCAL",
	NONE = "NONE",
}

/** How often the measurement is taken. */
export enum MeasurementFrequency {
	PER_PROTOCOL_DAY = "PER_PROTOCOL_DAY",
	PER_OCCURRENCE = "PER_OCCURRENCE",
	RUNNING_TOTAL = "RUNNING_TOTAL",
}

/** How a target is compared — and only how, never against what (AC11). */
export enum MeasurementComparator {
	NONE = "NONE",
	AT_OR_ABOVE = "AT_OR_ABOVE",
	AT_OR_BELOW = "AT_OR_BELOW",
}

/**
 * Who produces the value — AC8.
 *
 * Two values, deliberately not three: integration, engine and staff-entered all
 * collapse to "not the participant" because no consumer branches between them.
 */
export enum MeasurementProducedBy {
	PARTICIPANT = "PARTICIPANT",
	SYSTEM = "SYSTEM",
}

export interface Measurement {
	/** AC12 — optional. Absent, every surface derives the same name. */
	name?: string;
	/** AC12 — what is being counted, where the unit is a count. */
	countedNoun?: string;
	quantity: MeasurementQuantity;
	unit: MeasurementUnit;
	frequency: MeasurementFrequency;
	comparator: MeasurementComparator;
	producedBy: MeasurementProducedBy;
	/** AC6 / AC13 — a role id from this account's configured roles. */
	accessRequirement?: string;
	/** AC3 — display order on the event. */
	position: number;
}

/** The words a configurator reads, keyed by the value that is stored. */
export const QUANTITY_LABEL: Record<MeasurementQuantity, string> = {
	[MeasurementQuantity.PERCENTAGE_IN_RANGE]: "Percentage in range",
	[MeasurementQuantity.AVERAGE_VALUE]: "Average value",
	[MeasurementQuantity.SCORE]: "Score",
	[MeasurementQuantity.COUNT_OF_OCCURRENCES]: "Count of occurrences",
	[MeasurementQuantity.DURATION]: "Duration",
	[MeasurementQuantity.DISCRETE_OUTCOME]: "Discrete outcome",
};

export const UNIT_LABEL: Record<MeasurementUnit, string> = {
	[MeasurementUnit.PERCENT]: "Percent",
	[MeasurementUnit.HOURS]: "Hours",
	[MeasurementUnit.MINUTES]: "Minutes",
	[MeasurementUnit.COUNT]: "Count",
	[MeasurementUnit.MG_DL]: "mg/dL",
	[MeasurementUnit.KCAL]: "kcal",
	[MeasurementUnit.NONE]: "None",
};

export const FREQUENCY_LABEL: Record<MeasurementFrequency, string> = {
	[MeasurementFrequency.PER_PROTOCOL_DAY]: "Once per protocol day",
	[MeasurementFrequency.PER_OCCURRENCE]: "Once per occurrence",
	[MeasurementFrequency.RUNNING_TOTAL]: "Running total",
};

/**
 * AC1's third clause and AC4 — the comparator's words say where the value comes
 * from, so the absent number reads as deliberate rather than as a field someone
 * forgot to fill in.
 */
export const COMPARATOR_LABEL: Record<MeasurementComparator, string> = {
	[MeasurementComparator.NONE]: "No target",
	[MeasurementComparator.AT_OR_ABOVE]: "At or above target",
	[MeasurementComparator.AT_OR_BELOW]: "At or below target",
};

export const PRODUCED_BY_LABEL: Record<MeasurementProducedBy, string> = {
	[MeasurementProducedBy.PARTICIPANT]: "The participant",
	[MeasurementProducedBy.SYSTEM]: "The system",
};

/**
 * The name every surface shows when none was given — AC12.
 *
 * 🔴 Must match the server's `derivedMeasurementName` exactly. The criterion is
 * determinism across surfaces: the same measurement must never appear under one
 * name here and another on the dashboard. Two implementations of one rule is a
 * risk the ticket accepts to keep the list readable before a save; a change to
 * either belongs in both.
 */
export const derivedMeasurementName = (measurement: {
	quantity?: MeasurementQuantity;
	countedNoun?: string;
}): string => {
	if (measurement.countedNoun?.trim()) return measurement.countedNoun.trim();

	const words: Record<MeasurementQuantity, string> = {
		[MeasurementQuantity.PERCENTAGE_IN_RANGE]: "Percentage in range",
		[MeasurementQuantity.AVERAGE_VALUE]: "Average value",
		[MeasurementQuantity.SCORE]: "Score",
		[MeasurementQuantity.COUNT_OF_OCCURRENCES]: "Count",
		[MeasurementQuantity.DURATION]: "Duration",
		[MeasurementQuantity.DISCRETE_OUTCOME]: "Outcome",
	};

	return (
		(measurement.quantity ? words[measurement.quantity] : undefined) ??
		"Measurement"
	);
};
